import Dialog from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Label from "@/v2-ui/label"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import UserPicker from "@/v2-backoffice-client/user/UserPicker"
import { useSetCollectionOwnerMutation } from "@/v2-backoffice-client/license/collections/license.collectionOwner.set.mutation"
import { evictInvoiceCollectionQueries } from "@/v2-backoffice-client/license/license.utils"
import type { InvoiceCollectionsQuery } from "@/v2-backoffice-client/license/collections/__types__/license.collections.query"

export type LicenseCollectionOwnerDialogProps = {
  invoice: InvoiceCollectionsQuery["getInvoiceCollections"][number]
  open: boolean
  onOpenChange(open: boolean): void
}

// Reassigns who chases a customer's debt (C4): picking a user sets the
// operator-stamped backoffice override, which wins over the mirrored HubSpot
// company owner; clearing the override falls back to HubSpot (or to
// unassigned, which stays claimable on the board). Ownership is per
// CUSTOMER — one call covers everything they owe — so the change shows on
// all of the customer's rows.
function LicenseCollectionOwnerDialog(
  props: LicenseCollectionOwnerDialogProps,
) {
  const { invoice, open, onOpenChange } = props

  const debtorName =
    invoice.CustomerName ?? invoice.AccountCompanyName ?? "the customer"
  const isOverride = invoice.OwnerSource === "Backoffice"

  const [setOwner, { loading, error }] = useSetCollectionOwnerMutation({
    update: evictInvoiceCollectionQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Owner updated",
        description: `Collections ownership for ${debtorName} was updated.`,
      })
      onOpenChange(false)
    },
  })

  if (invoice.CustomerId == null) return null

  const customerId = invoice.CustomerId

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Collections owner — {debtorName}</Dialog.Title>
          <Dialog.Description>
            Who chases this customer&apos;s outstanding invoices. The default is
            the HubSpot company owner; an assignment made here wins and is
            operator-stamped. Ownership covers all of the customer&apos;s rows.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Current owner</Label>
            <p className="text-sm">
              {invoice.OwnerName ?? "Unassigned"}
              {invoice.OwnerName && (
                <span className="text-muted-foreground">
                  {isOverride
                    ? " — assigned in backoffice"
                    : " — HubSpot company owner"}
                </span>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Assign to</Label>
            <UserPicker
              onSelect={(user) => {
                void setOwner({
                  variables: { customerId, ownerUserId: user.Id },
                })
              }}
            />
          </div>
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          {isOverride && (
            <Button
              type="button"
              variant="outline"
              isLoading={loading}
              data-feature-code="UI_BackofficeCollections_Owner_Clear"
              onClick={() => {
                void setOwner({
                  variables: { customerId, ownerUserId: null },
                })
              }}
            >
              Use HubSpot Owner
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Close
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseCollectionOwnerDialog
